import { FunctionalComponent } from "preact";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  queryClient,
  createPersister,
  PERSIST_BUSTER,
} from "../lib/queryClient";
import { PERSISTED_KEY_ROOTS } from "../lib/queryKeys";

const persister = createPersister();

/**
 * Wraps the app in a React Query provider whose cache is hydrated from
 * localStorage on boot, so components render cached data on the first paint and
 * refetch in the background. Only the key roots in PERSISTED_KEY_ROOTS are
 * written to disk (see queryKeys.ts).
 */
const QueryProvider: FunctionalComponent = ({ children }) => {
  // No localStorage available (SSR/edge build prerender) — fall back to an
  // in-memory client with no persistence.
  if (!persister) {
    return (
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    );
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        buster: PERSIST_BUSTER,
        maxAge: 24 * 60 * 60 * 1000,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            const root = query.queryKey?.[0];
            if (
              query.state.status !== "success" ||
              typeof root !== "string" ||
              !PERSISTED_KEY_ROOTS.has(root)
            ) {
              return false;
            }
            // documentBrowse keys are ["documentBrowse", workspaceId,
            // portalId, params]. Persisting every page+filter combination the
            // user visits would grow localStorage unbounded and slow hydration,
            // so only the unfiltered first page (the landing view) is kept.
            if (root === "documentBrowse") {
              const params = query.queryKey?.[3] as
                | {
                    page?: number;
                    selectedKapps?: unknown[];
                    selectedTags?: unknown[];
                  }
                | undefined;
              const isFirstPage = !params?.page || params.page === 1;
              const hasNoFilters =
                !params?.selectedKapps?.length && !params?.selectedTags?.length;
              return isFirstPage && hasNoFilters;
            }
            return true;
          },
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
};

export default QueryProvider;
